import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const refFromUrl = searchParams.get("ref") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", password: "", role: "customer", referral_code: refFromUrl });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await register(form);
      if (u.welcome_coupon) {
        toast.success(`Account created! Use code ${u.welcome_coupon} for 10% off your first order.`, { duration: 8000 });
      } else {
        toast.success("Account created! Welcome aboard.");
      }
      nav(u.role === "designer" ? "/designer" : "/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-[#FEF3E2]/40">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-[#1a3c5e]/10 p-8">
        <h1 className="font-display text-2xl font-bold text-[#1a3c5e]">Create your account</h1>
        <p className="text-sm text-gray-500 mt-1">Order, track and download your designs in one place.</p>
        <form onSubmit={submit} className="space-y-3 mt-6">
          <input placeholder="Full Name *" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="register-name" className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
          <input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required data-testid="register-email" className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="register-phone" className="border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            <input placeholder="City" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} data-testid="register-city" className="border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
          </div>
          <input type="password" placeholder="Password (min 6) *" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={6} data-testid="register-password" className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
          <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="register-role" className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white">
            <option value="customer">I'm a homeowner / customer</option>
            <option value="designer">I'm a designer / architect</option>
          </select>
          {form.role === "customer" && (
            <input
              placeholder="Referral code (optional)"
              value={form.referral_code}
              onChange={(e) => setForm({...form, referral_code: e.target.value.toUpperCase()})}
              data-testid="register-referral-code"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
            />
          )}
          <button disabled={submitting} type="submit" data-testid="register-submit" className="w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-semibold rounded-xl shadow-md transition flex items-center justify-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Create Account
          </button>
        </form>
        <p className="text-sm text-gray-500 mt-5 text-center">
          Already have an account? <Link to="/login" className="text-[#F7941D] font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
